import { type IMatchConfigEntry, type IProceduralMapTfConfig, type StubTiledMapOptions, TF_TERRAIN_DELTA_MAX_TILE_COUNT } from '@nova/types';

/**
 * MAP geometry / presentation recipes for procedural Terraforming maps.
 * Keyed by lobby match config {@link IMatchConfigEntry.mapAssetName}. Match rules stay on `gamePlayConfig`.
 * Tile pixel size is the shared action-game `pxPerUnit`, not authored per map.
 */

/**
 * Classic left landing zone with grass and flower stations stacked on the right.
 * Omit origin for left-wall band placement; set origin `{ x, y }` to pin the camp's map-tile top-left.
 */
const DEFAULT_TF_BASECAMP = {
  width: 8,
  height: 8,
  grassStation: { x: 4, y: 0, width: 4, height: 4 },
  flowerStation: { x: 4, y: 4, width: 4, height: 4 },
  /** Centered on the station land stamp when nav and collision are built. */
  stationEntitySize: { width: 200, height: 200 }
} as const;

/**
 * Centered camp on the 25-player playable grid. `origin` is the camp's map-tile upper-left.
 * The grass station sits in the camp's top-left. The flower station sits toward the bottom-right.
 */
const COOP_25_TF_BASECAMP = {
  ...DEFAULT_TF_BASECAMP,
  width: 12,
  height: 8,
  origin: { x: 36, y: 31 },
  grassStation: { x: 0, y: 0, width: 4, height: 4 },
  flowerStation: { x: 8, y: 4, width: 4, height: 4 }
} as const;

/**
 * Centered camp. `origin` is the camp's map-tile upper-left.
 * Grass station sits in the top-left quadrant; flower station sits in the top-right.
 */
const COMPACT_TF_BASECAMP = {
  width: 8,
  height: 8,
  origin: { x: 11, y: 11 },
  grassStation: { x: 0, y: 0, width: 4, height: 4 },
  flowerStation: { x: 4, y: 0, width: 4, height: 4 },
  /** Centered on the station land stamp when nav and collision are built. */
  stationEntitySize: { width: 200, height: 200 }
} as const;

const DEFAULT_TF_SEED_BURST_PADS = {
  size: { width: 4, height: 4 },
  spawnBufferTiles: 2
} as const;

export const proceduralMapTfById = {
  'tf-coop-25': {
    id: 'tf-coop-25',
    widthTiles: 84,
    heightTiles: 70,
    fillLayoutId: 'singleRightAnglesAndLines',
    obstacleTileMaxCoverage: 0.3,
    minimumRouteWidthTiles: 2,
    minimumPlacementSizeTiles: 3,
    maximumPlacementSizeTiles: 16,
    basecamp: COOP_25_TF_BASECAMP,
    seedBurstPads: DEFAULT_TF_SEED_BURST_PADS,
    presentation: {
      groundTilesetKey: 'tf-ground-open',
      grassTilesetKey: 'tf-terrain-grass',
      obstacleTilesetKey: 'tf-obstacle'
    }
  },
  'tf-coop-compact': {
    id: 'tf-coop-compact',
    widthTiles: 30,
    heightTiles: 30,
    fillLayoutId: 'singleRightAnglesAndLines',
    obstacleTileMaxCoverage: 0.3,
    minimumRouteWidthTiles: 2,
    minimumPlacementSizeTiles: 3,
    maximumPlacementSizeTiles: 12,
    basecamp: COMPACT_TF_BASECAMP,
    seedBurstPads: DEFAULT_TF_SEED_BURST_PADS,
    presentation: {
      groundTilesetKey: 'tf-ground-open-compact',
      grassTilesetKey: 'tf-terrain-grass-compact',
      obstacleTilesetKey: 'tf-obstacle-compact'
    }
  }
} as const satisfies Record<string, IProceduralMapTfConfig>;

export type ProceduralMapTfId = keyof typeof proceduralMapTfById;

function isProceduralMapTfId(mapId: string): mapId is ProceduralMapTfId {
  return Object.prototype.hasOwnProperty.call(proceduralMapTfById, mapId);
}

export function getProceduralMapTfConfig(mapId: string): IProceduralMapTfConfig | undefined {
  return isProceduralMapTfId(mapId) ? proceduralMapTfById[mapId] : undefined;
}

export interface ProceduralStubResolveOptions {
  /** Shared action-game tile pitch (`pxPerUnit`). When omitted, the stub builder default applies. */
  tilePx?: number;
}

/** Stub builder options derived from a procedural MAP recipe (presentation grid, incl. border ring). */
export function proceduralMapTfToStubOptions(config: IProceduralMapTfConfig, options?: ProceduralStubResolveOptions): StubTiledMapOptions {
  const borderOutset = Math.max(0, Math.floor(config.presentation.borderOutsetTiles ?? 1));
  const widthTiles = config.widthTiles + (2 * borderOutset);
  const heightTiles = config.heightTiles + (2 * borderOutset);
  const tileCount = widthTiles * heightTiles;
  if (!Number.isSafeInteger(widthTiles) || widthTiles < 1 || !Number.isSafeInteger(heightTiles) || heightTiles < 1
    || !Number.isSafeInteger(tileCount) || tileCount > TF_TERRAIN_DELTA_MAX_TILE_COUNT) {
    throw new RangeError(
      `Terraforming map ${config.id} presentation grid ${widthTiles}x${heightTiles} has ${tileCount} tiles; ` +
      `the live terrain-delta format supports 1-${TF_TERRAIN_DELTA_MAX_TILE_COUNT}.`
    );
  }

  return {
    widthTiles,
    heightTiles,
    tilePx: options?.tilePx
  };
}
